/**
 * Date Range Presets Utility
 *
 * This module provides a flexible API for creating date range presets with reduced code repetition.
 *
 * ## Core Builder Functions
 *
 * Instead of dozens of individual preset functions, use these flexible builders:
 *
 * - **createRelativePeriod(unit, offset)** - Create relative periods like "this month", "last week", "next year"
 *   - Examples: createRelativePeriod('month', 0), createRelativePeriod('week', -1)
 *
 * - **createRollingPeriod(count, unit, direction)** - Create rolling periods like "last 30 days", "next 90 days"
 *   - Examples: createRollingPeriod(30, 'day', 'past'), createRollingPeriod(90, 'day', 'future')
 *
 * - **createPeriodToDate(unit)** - Create "to date" ranges like "month to date", "year to date"
 *   - Examples: createPeriodToDate('month'), createPeriodToDate('year')
 *
 * - **createSpecificPeriod(year, unit, period?)** - Create specific periods like "Q2 2024", "June 2024"
 *   - Examples: createSpecificPeriod(2024, 'quarter', 2), createSpecificPeriod(2024, 'month', 5)
 *
 * - **createCustomRange(startDate, endDate)** - Create custom date ranges
 *   - Example: createCustomRange(new Date('2024-01-01'), new Date('2024-12-31'))
 *
 * - **createFiscalYear(year, startMonth?)** - Create fiscal year ranges
 *   - Example: createFiscalYear(2024, 3) // FY starting in April
 *
 * ## Configuration
 *
 * Configure global behavior using `configureDateRangePresets()`:
 * - Set week start day (Sunday or Monday)
 * - Set fiscal year start month
 *
 * ## Common Presets
 *
 * The `dateRangePresets` object provides convenient shortcuts for common use cases,
 * all built using the core builder functions.
 *
 * @example
 * // Using builder functions for maximum flexibility
 * const last45Days = createRollingPeriod(45, 'day', 'past');
 * const lastQuarter = createRelativePeriod('quarter', -1);
 * const q3Of2024 = createSpecificPeriod(2024, 'quarter', 3);
 *
 * // Using convenient presets
 * const today = dateRangePresets.today;
 * const last30Days = dateRangePresets.last30Days;
 * const yearToDate = dateRangePresets.yearToDate;
 */

import type { DateRangeValue } from './types/date.types';

/**
 * Time unit types for date range calculations
 */
export type TimeUnit = 'day' | 'month' | 'quarter' | 'week' | 'year';

/**
 * Week type for start of week calculations
 */
export type WeekStartDay = 'monday' | 'sunday';

/**
 * Configuration options for date range presets
 */
export interface DateRangeConfig {
  /**
   * First month of fiscal year (0-11, default: 0 for January)
   */
  fiscalYearStartMonth?: number;
  /**
   * First day of the week (default: 'sunday')
   */
  weekStartsOn?: WeekStartDay;
}

// Global configuration
let config: DateRangeConfig = {
  fiscalYearStartMonth: 0,
  weekStartsOn: 'sunday',
};

/**
 * Configure date range preset behavior
 */
export function configureDateRangePresets(options: DateRangeConfig): void {
  config = { ...config, ...options };
}

// ============================================================================
// Internal Date Utility Functions
// ============================================================================

function startOfDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

function endOfDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}

function addDays(date: Date, days: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

function startOfWeek(date: Date, weekStartsOn: WeekStartDay = config.weekStartsOn ?? 'sunday'): Date {
  const newDate = new Date(date);
  const day = newDate.getDay();

  if (weekStartsOn === 'monday') {
    if (day === 0) {
      newDate.setDate(newDate.getDate() - 6);
    } else {
      newDate.setDate(newDate.getDate() - day + 1);
    }
  } else {
    newDate.setDate(newDate.getDate() - day);
  }

  return startOfDay(newDate);
}

function endOfWeek(date: Date, weekStartsOn: WeekStartDay = config.weekStartsOn ?? 'sunday'): Date {
  const start = startOfWeek(date, weekStartsOn);
  const end = addDays(start, 6);
  return endOfDay(end);
}

function startOfMonth(date: Date): Date {
  const newDate = new Date(date);
  newDate.setDate(1);
  return startOfDay(newDate);
}

function endOfMonth(date: Date): Date {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + 1, 0);
  return endOfDay(newDate);
}

function startOfQuarter(date: Date): Date {
  const newDate = new Date(date);
  const quarterStartMonth = Math.floor(newDate.getMonth() / 3) * 3;
  newDate.setMonth(quarterStartMonth, 1);
  return startOfDay(newDate);
}

function endOfQuarter(date: Date): Date {
  const start = startOfQuarter(date);
  const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
  return endOfDay(end);
}

function startOfYear(date: Date): Date {
  const newDate = new Date(date);
  newDate.setMonth(0, 1);
  return startOfDay(newDate);
}

function endOfYear(date: Date): Date {
  const newDate = new Date(date);
  newDate.setMonth(11, 31);
  return endOfDay(newDate);
}

// ============================================================================
// Core Builder Functions
// ============================================================================

/**
 * Creates a date range for a relative period (e.g., "this month", "last week", "next year")
 *
 * @param unit - The time unit (day, week, month, quarter, year)
 * @param offset - Offset from current period (0 = this, -1 = last, 1 = next)
 * @returns A function that returns the date range
 *
 * @example
 * createRelativePeriod('month', 0)  // This month
 * createRelativePeriod('week', -1)  // Last week
 * createRelativePeriod('year', 1)   // Next year
 */
export function createRelativePeriod(unit: TimeUnit, offset: number = 0): () => DateRangeValue {
  return () => {
    const now = new Date();
    let targetDate: Date;

    switch (unit) {
      case 'day': {
        targetDate = addDays(now, offset);
        return {
          end: endOfDay(targetDate),
          start: startOfDay(targetDate),
        };
      }
      case 'month': {
        targetDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        return {
          end: endOfMonth(targetDate),
          start: startOfMonth(targetDate),
        };
      }
      case 'quarter': {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const targetQuarter = currentQuarter + offset;
        const targetYear = now.getFullYear() + Math.floor(targetQuarter / 4);
        const targetQuarterInYear = ((targetQuarter % 4) + 4) % 4;
        targetDate = new Date(targetYear, targetQuarterInYear * 3, 1);
        return {
          end: endOfQuarter(targetDate),
          start: startOfQuarter(targetDate),
        };
      }
      case 'week': {
        targetDate = addDays(now, offset * 7);
        return {
          end: endOfWeek(targetDate),
          start: startOfWeek(targetDate),
        };
      }
      case 'year': {
        targetDate = new Date(now.getFullYear() + offset, 0, 1);
        return {
          end: endOfYear(targetDate),
          start: startOfYear(targetDate),
        };
      }
    }
  };
}

/**
 * Creates a rolling date range (e.g., "last 7 days", "next 30 days")
 *
 * @param count - Number of units
 * @param unit - The time unit (day, week, month, year)
 * @param direction - 'past' for last N units, 'future' for next N units
 * @returns A function that returns the date range
 *
 * @example
 * createRollingPeriod(7, 'day', 'past')    // Last 7 days
 * createRollingPeriod(3, 'month', 'past')  // Last 3 months
 * createRollingPeriod(30, 'day', 'future') // Next 30 days
 */
export function createRollingPeriod(
  count: number,
  unit: TimeUnit,
  direction: 'future' | 'past' = 'past',
): () => DateRangeValue {
  return () => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (unit) {
      case 'day': {
        if (direction === 'past') {
          start = startOfDay(addDays(now, -(count - 1)));
          end = endOfDay(now);
        } else {
          start = startOfDay(now);
          end = endOfDay(addDays(now, count - 1));
        }
        break;
      }

      case 'month': {
        if (direction === 'past') {
          start = startOfMonth(new Date(now.getFullYear(), now.getMonth() - (count - 1), 1));
          end = endOfDay(now);
        } else {
          start = startOfDay(now);
          end = endOfMonth(new Date(now.getFullYear(), now.getMonth() + (count - 1), 1));
        }
        break;
      }

      case 'quarter': {
        const currentQuarter = Math.floor(now.getMonth() / 3);

        if (direction === 'past') {
          const startQuarter = currentQuarter - (count - 1);
          const startYear = now.getFullYear() + Math.floor(startQuarter / 4);
          const startQuarterInYear = ((startQuarter % 4) + 4) % 4;
          start = startOfQuarter(new Date(startYear, startQuarterInYear * 3, 1));
          end = endOfDay(now);
        } else {
          const endQuarter = currentQuarter + (count - 1);
          const endYear = now.getFullYear() + Math.floor(endQuarter / 4);
          const endQuarterInYear = ((endQuarter % 4) + 4) % 4;
          start = startOfDay(now);
          end = endOfQuarter(new Date(endYear, endQuarterInYear * 3, 1));
        }
        break;
      }

      case 'week': {
        if (direction === 'past') {
          start = startOfWeek(addDays(now, -7 * (count - 1)));
          end = endOfWeek(now);
        } else {
          start = startOfWeek(now);
          end = endOfWeek(addDays(now, 7 * (count - 1)));
        }
        break;
      }

      case 'year': {
        if (direction === 'past') {
          start = startOfYear(new Date(now.getFullYear() - (count - 1), 0, 1));
          end = endOfDay(now);
        } else {
          start = startOfDay(now);
          end = endOfYear(new Date(now.getFullYear() + (count - 1), 0, 1));
        }
        break;
      }
    }

    return { end, start };
  };
}

/**
 * Creates a "period to date" range (e.g., "month to date", "year to date")
 *
 * @param unit - The time unit (week, month, quarter, year)
 * @returns A function that returns the date range from start of period to now
 *
 * @example
 * createPeriodToDate('month')   // Month to date
 * createPeriodToDate('year')    // Year to date
 * createPeriodToDate('quarter') // Quarter to date
 */
export function createPeriodToDate(unit: Exclude<TimeUnit, 'day'>): () => DateRangeValue {
  return () => {
    const now = new Date();
    let start: Date;

    switch (unit) {
      case 'month': {
        start = startOfMonth(now);
        break;
      }
      case 'quarter': {
        start = startOfQuarter(now);
        break;
      }
      case 'week': {
        start = startOfWeek(now);
        break;
      }
      case 'year': {
        start = startOfYear(now);
        break;
      }
    }

    return {
      end: endOfDay(now),
      start,
    };
  };
}

/**
 * Creates a date range for a specific absolute period
 *
 * @param year - The year
 * @param unit - The time unit
 * @param period - The period number (month: 0-11, quarter: 1-4, week: 1-53)
 * @returns A function that returns the date range
 *
 * @example
 * createSpecificPeriod(2024, 'year')           // Entire year 2024
 * createSpecificPeriod(2024, 'quarter', 2)     // Q2 2024
 * createSpecificPeriod(2024, 'month', 5)       // June 2024 (month 5)
 */
export function createSpecificPeriod(year: number, unit: TimeUnit, period?: number): () => DateRangeValue {
  return () => {
    switch (unit) {
      case 'day': {
        if (period === undefined || period < 1 || period > 366) {
          throw new Error('Day must be between 1 and 366');
        }
        const dayDate = new Date(year, 0, period);
        return {
          end: endOfDay(dayDate),
          start: startOfDay(dayDate),
        };
      }
      case 'month': {
        if (period === undefined || period < 0 || period > 11) {
          throw new Error('Month must be between 0 and 11');
        }
        const monthStart = new Date(year, period, 1);
        return {
          end: endOfMonth(monthStart),
          start: startOfMonth(monthStart),
        };
      }
      case 'quarter': {
        if (period === undefined || period < 1 || period > 4) {
          throw new Error('Quarter must be between 1 and 4');
        }
        const quarterStartMonth = (period - 1) * 3;
        const quarterStart = new Date(year, quarterStartMonth, 1);
        return {
          end: endOfQuarter(quarterStart),
          start: startOfQuarter(quarterStart),
        };
      }
      case 'week': {
        if (period === undefined || period < 1 || period > 53) {
          throw new Error('Week must be between 1 and 53');
        }
        const jan4 = new Date(year, 0, 4);
        const jan4Day = jan4.getDay() || 7;
        const weekStart = new Date(jan4);
        weekStart.setDate(jan4.getDate() - jan4Day + 1 + (period - 1) * 7);
        const weekEnd = addDays(weekStart, 6);
        return {
          end: endOfDay(weekEnd),
          start: startOfDay(weekStart),
        };
      }
      case 'year': {
        return {
          end: endOfYear(new Date(year, 0, 1)),
          start: startOfYear(new Date(year, 0, 1)),
        };
      }
    }
  };
}

/**
 * Creates a custom date range between two specific dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @returns A function that returns the date range
 *
 * @example
 * createCustomRange(new Date('2024-01-01'), new Date('2024-12-31'))
 */
export function createCustomRange(startDate: Date, endDate: Date): () => DateRangeValue {
  return () => ({
    end: endOfDay(new Date(endDate)),
    start: startOfDay(new Date(startDate)),
  });
}

/**
 * Creates a fiscal year date range
 *
 * @param year - The fiscal year
 * @param startMonth - Starting month of fiscal year (0-11, defaults to configured value)
 * @returns A function that returns the date range
 *
 * @example
 * createFiscalYear(2024)        // Uses configured fiscal year start
 * createFiscalYear(2024, 3)     // Fiscal year starting in April
 */
export function createFiscalYear(year: number, startMonth?: number): () => DateRangeValue {
  return () => {
    const fiscalStartMonth = startMonth ?? config.fiscalYearStartMonth ?? 0;
    const start = new Date(year, fiscalStartMonth, 1);
    const end = new Date(year + 1, fiscalStartMonth, 0);
    return {
      end: endOfDay(end),
      start: startOfDay(start),
    };
  };
}

// ============================================================================
// Common Presets (Convenience Shortcuts)
// ============================================================================

/**
 * Common date range preset functions using the builder API
 */
export const dateRangePresets = {
  // Special ranges
  allTime: (): DateRangeValue => {
    const now = new Date();
    return {
      end: endOfYear(now),
      start: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
    };
  },

  // Rolling periods (past)
  last2Weeks: createRollingPeriod(2, 'week', 'past'),
  last2Years: createRollingPeriod(2, 'year', 'past'),
  last3Months: createRollingPeriod(3, 'month', 'past'),
  last6Months: createRollingPeriod(6, 'month', 'past'),
  last7Days: createRollingPeriod(7, 'day', 'past'),
  last12Months: createRollingPeriod(12, 'month', 'past'),
  last14Days: createRollingPeriod(14, 'day', 'past'),
  last30Days: createRollingPeriod(30, 'day', 'past'),
  last60Days: createRollingPeriod(60, 'day', 'past'),
  last90Days: createRollingPeriod(90, 'day', 'past'),
  last180Days: createRollingPeriod(180, 'day', 'past'),
  last365Days: createRollingPeriod(365, 'day', 'past'),

  // Months
  lastMonth: createRelativePeriod('month', -1),

  // Quarters
  lastQuarter: createRelativePeriod('quarter', -1),

  // Weeks
  lastWeek: createRelativePeriod('week', -1),

  // Years
  lastYear: createRelativePeriod('year', -1),

  lifetime: (): DateRangeValue => ({
    end: endOfYear(new Date()),
    start: new Date(1970, 0, 1, 0, 0, 0, 0),
  }),

  // Period to date
  monthToDate: createPeriodToDate('month'),

  // Rolling periods (future)
  next7Days: createRollingPeriod(7, 'day', 'future'),
  next14Days: createRollingPeriod(14, 'day', 'future'),
  next30Days: createRollingPeriod(30, 'day', 'future'),
  next90Days: createRollingPeriod(90, 'day', 'future'),

  // Months
  nextMonth: createRelativePeriod('month', 1),

  // Quarters
  nextQuarter: createRelativePeriod('quarter', 1),

  // Weeks
  nextWeek: createRelativePeriod('week', 1),

  // Years
  nextYear: createRelativePeriod('year', 1),

  // Period to date
  quarterToDate: createPeriodToDate('quarter'),

  // Months
  thisMonth: createRelativePeriod('month', 0),

  // Quarters
  thisQuarter: createRelativePeriod('quarter', 0),

  // Weeks
  thisWeek: createRelativePeriod('week', 0),

  // Years
  thisYear: createRelativePeriod('year', 0),

  // Single days
  today: createRelativePeriod('day', 0),
  tomorrow: createRelativePeriod('day', 1),

  // Period to date
  weekToDate: createPeriodToDate('week'),

  // Single days
  yesterday: createRelativePeriod('day', -1),

  // Period to date
  yearToDate: createPeriodToDate('year'),
};
